/**
 * Client Vessels Hook
 *
 * DEPRECATED: Legacy Supabase implementation
 * Phase 6 (2026-02-11): Azure Migration Complete - This hook is no longer used
 * TODO: Remove this file after confirming no dependencies
 *
 * Direct Supabase queries for managing organization vessels.
 * Phase 3: Full CRUD operations (create, read, update, delete)
 * - Any organization member can view vessels
 * - Only OWNER/ADMIN can mutate vessels
 */

import { useState, useEffect, useCallback } from 'react';
// DEPRECATED: Supabase removed in Phase 6 Azure migration
// import { getSupabaseClient } from '../../lib/supabase';
import { usePermissions } from '../usePermissions';

/**
 * Map Supabase errors to user-friendly messages
 */
const mapErrorToMessage = (error) => {
  if (!error) return 'Unknown error occurred';

  const message = error.message || String(error);

  // Permission errors
  if (message.includes('permission denied') || message.includes('RLS')) {
    return 'Access denied: You do not have permission to perform this action';
  }

  // Unique constraint violations
  if (message.includes('duplicate key') || message.includes('unique constraint')) {
    if (message.includes('imo_number')) {
      return 'A vessel with this IMO number already exists';
    }
    return 'This vessel already exists';
  }

  // Foreign key violations
  if (message.includes('foreign key')) {
    return 'Invalid organization reference';
  }

  // Not found errors
  if (message.includes('No rows found') || error.code === 'PGRST116') {
    return 'Vessel not found';
  }

  // Network errors
  if (message.includes('Failed to fetch') || message.includes('network')) {
    return 'Network error: Please check your connection';
  }

  // Return original message if no match
  return message;
};

export const useClientVessels = (organizationId) => {
  const { currentUser } = usePermissions();
  const [vessels, setVessels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Permission check: User must be a member of the organization
  const canView = useCallback(async () => {
    if (!organizationId || !currentUser?.id) return false;

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return false;

      const { data } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', organizationId)
        .eq('user_id', currentUser.id)
        .single();

      // Any member can view (OWNER/ADMIN/SKIPPER/CREW)
      return data !== null;
    } catch (err) {
      console.error('Permission check error:', err);
      return false;
    }
  }, [organizationId, currentUser]);

  // Fetch vessels for organization
  const fetchVessels = useCallback(async () => {
    const hasPermission = await canView();
    if (!hasPermission) {
      setError('Access denied: Not a member of this organization');
      setIsLoading(false);
      return;
    }

    if (!organizationId) {
      setVessels([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      // Query vessels table
      const { data, error: queryError } = await supabase
        .from('vessels')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      setVessels(data || []);
    } catch (err) {
      console.error('Error fetching vessels:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, canView]);

  // Fetch on org change
  useEffect(() => {
    fetchVessels();
  }, [fetchVessels]);

  // Permission check for mutations: Only OWNER/ADMIN can create/update/delete
  const canMutate = useCallback(async () => {
    if (!organizationId || !currentUser?.id) return false;

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return false;

      const { data } = await supabase
        .from('organization_members')
        .select('role')
        .eq('organization_id', organizationId)
        .eq('user_id', currentUser.id)
        .single();

      // Only OWNER/ADMIN can mutate
      return data && ['OWNER', 'ADMIN'].includes(data.role);
    } catch (err) {
      console.error('Permission check error:', err);
      return false;
    }
  }, [organizationId, currentUser]);

  // Create vessel
  const createVessel = useCallback(async (vesselData) => {
    const hasPerm = await canMutate();
    if (!hasPerm) {
      const errorMsg = 'Access denied: OWNER or ADMIN role required';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      const { data, error: insertError } = await supabase
        .from('vessels')
        .insert([{
          organization_id: organizationId, // Auto-scope to org
          name: vesselData.name,
          imo_number: vesselData.imo_number || null,
          flag_state: vesselData.flag_state || null,
          vessel_type: vesselData.vessel_type || null
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      await fetchVessels(); // Refresh list
      return { success: true, data };
    } catch (err) {
      console.error('Error creating vessel:', err);
      const errorMsg = mapErrorToMessage(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [canMutate, organizationId, fetchVessels]);

  // Update vessel
  const updateVessel = useCallback(async (vesselId, updates) => {
    const hasPerm = await canMutate();
    if (!hasPerm) {
      const errorMsg = 'Access denied: OWNER or ADMIN role required';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      const { data, error: updateError } = await supabase
        .from('vessels')
        .update({
          name: updates.name,
          imo_number: updates.imo_number || null,
          flag_state: updates.flag_state || null,
          vessel_type: updates.vessel_type || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', vesselId)
        .eq('organization_id', organizationId) // Security: ensure same org
        .select()
        .single();

      if (updateError) throw updateError;

      await fetchVessels(); // Refresh list
      return { success: true, data };
    } catch (err) {
      console.error('Error updating vessel:', err);
      const errorMsg = mapErrorToMessage(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [canMutate, organizationId, fetchVessels]);

  // Delete vessel
  const deleteVessel = useCallback(async (vesselId) => {
    const hasPerm = await canMutate();
    if (!hasPerm) {
      const errorMsg = 'Access denied: OWNER or ADMIN role required';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        throw new Error('Supabase not configured');
      }

      const { error: deleteError } = await supabase
        .from('vessels')
        .delete()
        .eq('id', vesselId)
        .eq('organization_id', organizationId); // Security: ensure same org

      if (deleteError) throw deleteError;

      await fetchVessels(); // Refresh list
      return { success: true };
    } catch (err) {
      console.error('Error deleting vessel:', err);
      const errorMsg = mapErrorToMessage(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }, [canMutate, organizationId, fetchVessels]);

  return {
    vessels,
    isLoading,
    error,
    createVessel,
    updateVessel,
    deleteVessel,
    refresh: fetchVessels
  };
};

export default useClientVessels;
